/**
 * Package Store - Zustand
 *
 * Centralized state management for subscription packages
 *
 * Features:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Real-time search and filtering
 * - Pagination support
 * - Sorting capabilities
 * - Loading and error states
 * - Optimistic UI updates
 * - Toast notifications
 *
 * Backend API: /api/packages
 *
 * @module usePackageStore
 */

import { create } from 'zustand'
import { packageService } from '@services/packageService'
import toast from 'react-hot-toast'

/**
 * Package Store
 *
 * State Management for Subscription Packages
 */
export const usePackageStore = create((set, get) => ({
	// ============================================================================
	// STATE
	// ============================================================================

	/**
	 * List of all packages
	 * @type {Array<Object>}
	 */
	packages: [],

	/**
	 * Currently selected package for viewing/editing
	 * @type {Object|null}
	 */
	selectedPackage: null,

	/**
	 * Loading state for async operations
	 * @type {boolean}
	 */
	loading: false,

	/**
	 * Error message if operation fails
	 * @type {string|null}
	 */
	error: null,

	/**
	 * Filter and search parameters
	 * @type {Object}
	 */
	filters: {
		search: '', // Search by title/description
		status: 'all', // all, active, archived
		billing_cycle: 'all', // all, monthly, yearly
		sortBy: 'title', // Field to sort by
		sortOrder: 'asc', // asc or desc
	},

	/**
	 * Pagination configuration
	 * @type {Object}
	 */
	pagination: {
		page: 1, // Current page
		limit: 10, // Items per page
		total: 0, // Total number of items
		totalPages: 0, // Total number of pages
	},

	/**
	 * Statistics about packages
	 * @type {Object}
	 */
	stats: {
		total: 0,
		active: 0,
		archived: 0,
	},

	// ============================================================================
	// ACTIONS - READ OPERATIONS
	// ============================================================================

	/**
	 * Fetch all packages with current filters
	 *
	 * @returns {Promise<void>}
	 */
	fetchPackages: async () => {
		set({ loading: true, error: null })

		try {
			const { filters, pagination } = get()

			// Build query parameters
			const params = {
				page: pagination.page,
				limit: pagination.limit,
			}

			if (filters.search) {
				params.search = filters.search
			}

			if (filters.status !== 'all') {
				params.status = filters.status
			}

			if (filters.billing_cycle !== 'all') {
				params.billing_cycle = filters.billing_cycle
			}

			// Make API request
			const response = await packageService.getAll(params)

			// Backend returns { status, message, data: [...], meta: {...} }
			const packages = response.data || []
			const meta = response.meta || {}

			// Update state with response data
			set({
				packages: packages,
				pagination: {
					...pagination,
					page: meta.page || pagination.page,
					total: meta.total || 0,
					totalPages: meta.total_pages || Math.ceil((meta.total || 0) / pagination.limit),
				},
				loading: false,
			})

			// Update statistics
			get().calculateStats()
		} catch (error) {
			console.error('Failed to fetch packages:', error)
			set({
				error: error.message || 'Failed to load packages',
				loading: false,
			})
			toast.error('Failed to load packages')
		}
	},

	/**
	 * Fetch single package by ID
	 *
	 * @param {number} id - Package ID
	 * @returns {Promise<Object>} Package data
	 */
	fetchPackageById: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await packageService.getById(id)
			const pkg = response.data || response

			set({
				selectedPackage: pkg,
				loading: false,
			})

			return pkg
		} catch (error) {
			console.error('Failed to fetch package:', error)
			set({
				error: error.message || 'Package not found',
				loading: false,
			})
			toast.error('Package not found')
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - CREATE OPERATIONS
	// ============================================================================

	/**
	 * Create new package
	 *
	 * @param {Object} data - Package data
	 * @returns {Promise<Object>} Created package
	 */
	createPackage: async (data) => {
		set({ loading: true, error: null })

		try {
			const response = await packageService.create(data)
			const newPackage = response.data || response

			// Optimistic UI update - add to beginning of list
			set((state) => ({
				packages: [newPackage, ...state.packages],
				pagination: {
					...state.pagination,
					total: state.pagination.total + 1,
				},
				loading: false,
			}))

			toast.success('Package created successfully!')

			// Update statistics
			get().calculateStats()

			return newPackage
		} catch (error) {
			console.error('Failed to create package:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to create package'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - UPDATE OPERATIONS
	// ============================================================================

	/**
	 * Update existing package
	 *
	 * @param {number} id - Package ID
	 * @param {Object} data - Updated package data
	 * @returns {Promise<Object>} Updated package
	 */
	updatePackage: async (id, data) => {
		set({ loading: true, error: null })

		try {
			const response = await packageService.update(id, data)
			const updatedPackage = response.data || response

			// Optimistic UI update - replace in list
			set((state) => ({
				packages: state.packages.map((pkg) => (pkg.id == id ? updatedPackage : pkg)),
				selectedPackage: state.selectedPackage?.id == id ? updatedPackage : state.selectedPackage,
				loading: false,
			}))

			toast.success('Package updated successfully!')

			// Update statistics
			get().calculateStats()

			return updatedPackage
		} catch (error) {
			console.error('Failed to update package:', error)

			// Extract detailed error message
			let errorMessage = 'Failed to update package'

			if (error.response?.data) {
				const responseData = error.response.data

				if (responseData.messages) {
					if (typeof responseData.messages === 'object') {
						const errors = Object.values(responseData.messages).filter(
							(msg) => typeof msg === 'string'
						)
						if (errors.length > 0) {
							errorMessage = errors.join(', ')
						}
					} else if (typeof responseData.messages === 'string') {
						errorMessage = responseData.messages
					}
				} else if (responseData.message) {
					errorMessage = responseData.message
				}
			} else if (error.message) {
				errorMessage = error.message
			}

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - ARCHIVE & DELETE OPERATIONS
	// ============================================================================

	/**
	 * Archive package (status only)
	 * Sets status to 'archived' without soft delete
	 *
	 * @param {number} id - Package ID
	 * @returns {Promise<void>}
	 */
	archivePackage: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await packageService.archive(id)
			const archived = response.data || response

			// Optimistic UI update - mark as archived (status only)
			set((state) => ({
				packages: state.packages.map((pkg) =>
					pkg.id == id ? { ...pkg, status: 'archived' } : pkg
				),
				selectedPackage: state.selectedPackage?.id == id ? null : state.selectedPackage,
				loading: false,
			}))

			toast.success('Package archived successfully!')

			// Update statistics
			get().calculateStats()

			// Refresh to apply filters
			setTimeout(() => get().fetchPackages(), 100)
		} catch (error) {
			console.error('Failed to archive package:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to archive package'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	/**
	 * Delete package (soft delete)
	 * Sets status to 'archived' AND deleted_at timestamp
	 *
	 * @param {number} id - Package ID
	 * @returns {Promise<void>}
	 */
	deletePackage: async (id) => {
		set({ loading: true, error: null })

		try {
			await packageService.delete(id)

			// Optimistic UI update - mark as archived and set deleted_at
			set((state) => ({
				packages: state.packages.map((pkg) =>
					pkg.id == id ? { ...pkg, status: 'archived', deleted_at: new Date().toISOString() } : pkg
				),
				selectedPackage: state.selectedPackage?.id == id ? null : state.selectedPackage,
				loading: false,
			}))

			toast.success('Package archived successfully!')

			// Update statistics
			get().calculateStats()

			// Refresh to apply filters (archived packages may be hidden based on filter)
			setTimeout(() => get().fetchPackages(), 100)
		} catch (error) {
			console.error('Failed to delete package:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to delete package'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	/**
	 * Restore archived package
	 *
	 * @param {number} id - Package ID
	 * @returns {Promise<void>}
	 */
	restorePackage: async (id) => {
		set({ loading: true, error: null })

		try {
			const response = await packageService.restore(id)
			const restored = response.data || response

			// Optimistic UI update - update existing package in list
			set((state) => ({
				packages: state.packages.map((pkg) =>
					pkg.id == id ? { ...pkg, status: 'active', deleted_at: null } : pkg
				),
				selectedPackage: state.selectedPackage?.id == id ? null : state.selectedPackage,
				loading: false,
			}))

			toast.success('Package restored successfully!')

			// Update statistics
			get().calculateStats()

			// Refresh to apply filters
			setTimeout(() => get().fetchPackages(), 100)
		} catch (error) {
			console.error('Failed to restore package:', error)

			const errorMessage = error.response?.data?.message || error.message || 'Failed to restore package'

			set({
				error: errorMessage,
				loading: false,
			})

			toast.error(errorMessage)
			throw error
		}
	},

	// ============================================================================
	// ACTIONS - FILTER & SEARCH
	// ============================================================================

	/**
	 * Update search/filter parameters and refresh data
	 *
	 * @param {Object} newFilters - New filter values
	 */
	setFilters: (newFilters) => {
		set((state) => ({
			filters: { ...state.filters, ...newFilters },
			pagination: { ...state.pagination, page: 1 }, // Reset to page 1 on filter change
		}))

		// Fetch with new filters
		get().fetchPackages()
	},

	/**
	 * Update search term
	 *
	 * @param {string} searchTerm - Search query
	 */
	setSearch: (searchTerm) => {
		get().setFilters({ search: searchTerm })
	},

	/**
	 * Update status filter
	 *
	 * @param {string} status - Status filter (all, active, archived)
	 */
	setStatusFilter: (status) => {
		get().setFilters({ status })
	},

	/**
	 * Update billing cycle filter
	 *
	 * @param {string} billingCycle - Billing cycle filter (all, monthly, yearly)
	 */
	setBillingCycleFilter: (billingCycle) => {
		get().setFilters({ billing_cycle: billingCycle })
	},

	/**
	 * Clear all filters
	 */
	clearFilters: () => {
		set({
			filters: {
				search: '',
				status: 'all',
				billing_cycle: 'all',
				sortBy: 'title',
				sortOrder: 'asc',
			},
			pagination: {
				...get().pagination,
				page: 1,
			},
		})

		get().fetchPackages()
	},

	// ============================================================================
	// ACTIONS - PAGINATION
	// ============================================================================

	/**
	 * Go to specific page
	 *
	 * @param {number} page - Page number
	 */
	setPage: (page) => {
		set((state) => ({
			pagination: { ...state.pagination, page },
		}))

		get().fetchPackages()
	},

	/**
	 * Change items per page
	 *
	 * @param {number} limit - Items per page
	 */
	setPageLimit: (limit) => {
		set((state) => ({
			pagination: { ...state.pagination, limit, page: 1 },
		}))

		get().fetchPackages()
	},

	/**
	 * Go to next page
	 */
	nextPage: () => {
		const { pagination } = get()
		if (pagination.page < pagination.totalPages) {
			get().setPage(pagination.page + 1)
		}
	},

	/**
	 * Go to previous page
	 */
	prevPage: () => {
		const { pagination } = get()
		if (pagination.page > 1) {
			get().setPage(pagination.page - 1)
		}
	},

	// ============================================================================
	// ACTIONS - UTILITIES
	// ============================================================================

	/**
	 * Calculate statistics from all packages (not just filtered view)
	 * Fetches counts directly to ensure accuracy
	 */
	calculateStats: async () => {
		try {
			// Fetch all packages without filters to get accurate counts
			const [allResponse, activeResponse, archivedResponse] = await Promise.all([
				packageService.getAll({ limit: 1 }), // Just get count
				packageService.getAll({ status: 'active', limit: 1 }),
				packageService.getAll({ status: 'archived', limit: 1 }),
			])

			const stats = {
				total: allResponse.meta?.total || 0,
				active: activeResponse.meta?.total || 0,
				archived: archivedResponse.meta?.total || 0,
			}

			set({ stats })
		} catch (error) {
			console.error('Failed to calculate stats:', error)
			// Fallback to local calculation
			const { packages } = get()
			const stats = {
				total: packages.length,
				active: packages.filter((pkg) => pkg.status === 'active').length,
				archived: packages.filter((pkg) => pkg.status === 'archived' || pkg.deleted_at).length,
			}
			set({ stats })
		}
	},

	/**
	 * Select package for viewing/editing
	 *
	 * @param {Object|null} pkg - Package to select
	 */
	selectPackage: (pkg) => {
		set({ selectedPackage: pkg })
	},

	/**
	 * Clear selected package
	 */
	clearSelection: () => {
		set({ selectedPackage: null })
	},

	/**
	 * Reset entire store to initial state
	 */
	reset: () => {
		set({
			packages: [],
			selectedPackage: null,
			loading: false,
			error: null,
			filters: {
				search: '',
				status: 'all',
				billing_cycle: 'all',
				sortBy: 'title',
				sortOrder: 'asc',
			},
			pagination: {
				page: 1,
				limit: 10,
				total: 0,
				totalPages: 0,
			},
			stats: {
				total: 0,
				active: 0,
				archived: 0,
			},
		})
	},
}))

export default usePackageStore
